import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, Home, Loader2, LockOpen, MapPin, RotateCcw, Scissors, Undo2, PackageOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import MoveMaterialDialog from "./MoveMaterialDialog";
import MaterialPhotos from "./MaterialPhotos";
import DestinationPicker, { destBody, emptyDest, resolveDest, useDestinations } from "./DestinationPicker";
import { ReopenPrompt } from "./TicketEditActions";

const sel = "h-7 text-xs border border-gray-300 rounded px-1 bg-white";
const RETURN_REASONS = ["excess", "wrong_material", "damaged", "other"];

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fallback;
};

const when = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric",
                                                         hour: "2-digit", minute: "2-digit" }) : "—");

// Where material is now. Used by Split.
const LocationPicker = ({ value, onChange, rooms, areas, testId }) => (
  <span className="inline-flex flex-wrap items-center gap-1">
    <select className={sel} value={value.location_type} data-testid={`${testId}-type`}
            onChange={(e) => onChange({ ...value, location_type: e.target.value })}>
      <option value="room">A room</option>
      <option value="area">An area</option>
      <option value="note">Somewhere else (type it)</option>
    </select>
    {value.location_type === "room" && (
      <select className={sel} value={value.room_key} data-testid={`${testId}-room`}
              onChange={(e) => onChange({ ...value, room_key: e.target.value })}>
        <option value="">Pick a room…</option>
        {rooms.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
      </select>
    )}
    {value.location_type === "area" && (
      <select className={sel} value={value.area} data-testid={`${testId}-area`}
              onChange={(e) => onChange({ ...value, area: e.target.value })}>
        <option value="">Pick an area…</option>
        {areas.map((a) => <option key={a} value={a}>{a}</option>)}
      </select>
    )}
    {value.location_type !== "room" && (
      <Input className="h-7 w-44 text-xs" placeholder="north stairwell landing"
             value={value.note} data-testid={`${testId}-note`}
             onChange={(e) => onChange({ ...value, note: e.target.value })} />
    )}
  </span>
);

/**
 * FLD-4 — what is behind a LOCATION cell: every placement in this room with its
 * quantity, product, location, who put it there and when. From here material can
 * be moved, split or sent back to the shop.
 */
export default function PlacementCard({ projectId, room, items = [], actor, role = "", viewFilter, onClose, onChanged, onPhotosChanged, deliveryRoom = null }) {
  const [photosFor, setPhotosFor] = useState(null);   // { placement } — this product at this location
  const [moveFor, setMoveFor] = useState(null);              // { entries, mode, title } — the shared move sheet
  const [reopenFor, setReopenFor] = useState(null);          // { placement, qty } — installed quantity reopen
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState({});
  const [openFor, setOpenFor] = useState(null);      // { id, action }
  const [loc, setLoc] = useState({ location_type: "note", room_key: "", area: "", note: "" });
  const [qty, setQty] = useState("");
  const [ret, setRet] = useState({ qty: "", reason: "excess", note: "" });

  const keys = items.map((i) => i.frs_item_key).filter(Boolean).join(",");
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/material/placements/detail",
        { params: { project_id: projectId, frs_item_keys: keys } });
      setData(d);
    } catch (e) {
      toast.error(errText(e, "Could not read the placements"));
      onClose?.();
    }
  }, [projectId, keys, onClose]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.placements || [];
  const pickedIds = Object.keys(picked).filter((k) => picked[k]);
  const reset = async (msg, description) => {
    toast.success(msg, { description });
    setOpenFor(null);
    setPicked({});
    setQty("");
    setRet({ qty: "", reason: "excess", note: "" });
    setLoc({ location_type: "note", room_key: "", area: "", note: "" });
    setDest(emptyDest);
    await load();
    onChanged?.();
  };

  const doSplit = async (id) => {
    setBusy(true);
    try {
      await api.post(`/material/placements/${id}/split`,
        { ...loc, qty: Number(qty || 0), by: actor });
      await reset("Placement split", "Both locations keep their own history");
    } catch (e) { toast.error(errText(e, "Could not split the placement")); }
    finally { setBusy(false); }
  };

  const movableIds = pickedIds.filter((id) => (rows.find((p) => p.id === id)?.movable_qty || 0) > 0);
  const movableUnits = movableIds.reduce((a, id) => a + (rows.find((p) => p.id === id)?.movable_qty || 0), 0);
  const [bulk, setBulk] = useState("");                // "" | "move" | "return"

  // FLD-13 — the shared destination picker drives the bulk move.
  const [dest, setDest] = useState(emptyDest);
  const entryOf = (p) => ({ placement_id: p.id, description: p.description || p.product_code || p.product_short || "",
                            uom: p.uom, available: p.movable_qty, qty: p.movable_qty, from_location: p.location_label });
  const pickedEntries = movableIds.map((id) => entryOf(rows.find((p) => p.id === id)));
  const destData = useDestinations(projectId, pickedIds, viewFilter);
  const resolved = resolveDest(dest, destData, pickedEntries);
  const okToMove = resolved.filter((r) => !r.flagged && !r.noop);
  const [moves, setMoves] = useState([]);              // grouped moves still undoable
  const loadMoves = useCallback(() => api.get("/material/move/recent", { params: { project_id: projectId } })
    .then(({ data: d }) => setMoves(d.moves.filter((m) => !m.undone))).catch(() => setMoves([])), [projectId]);
  useEffect(() => { loadMoves(); }, [loadMoves]);
  const undoableFor = (pid) => moves.find((m) => (m.lines || []).some((l) => l.new_placement_id === pid));

  const doGroupMove = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/material/move", {
        project_id: projectId, by: actor, confirm: true, dest: destBody(dest, destData),
        entries: okToMove.map((r) => ({ placement_id: r.placement_id, qty: Number(r.qty), room_key: r.to_room_key || "" })) });
      await reset(`${res.units} unit(s) moved`, res.message);
      setBulk(""); await loadMoves();
    } catch (e) { toast.error(errText(e, "Could not move the selection")); }
    finally { setBusy(false); }
  };

  const doUndo = async (moveId) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/material/move/${moveId}/undo`, { by: actor });
      await reset("Move undone", res.message);
      await loadMoves();
    } catch (e) { toast.error(errText(e, "Could not undo that move")); }
    finally { setBusy(false); }
  };

  const allPicked = rows.length > 0 && rows.every((p) => picked[p.id]);
  const toggleAll = () => setPicked(allPicked ? {} : Object.fromEntries(rows.map((p) => [p.id, true])));

  const doBulkReturn = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/material/placements/bulk-return",
        { placement_ids: pickedIds, reason: ret.reason, note: ret.note, by: actor });
      await reset(`${res.units} unit(s) heading back to the shop`,
                  "Installed units stayed; nothing is on hand again until the warehouse confirms receipt");
      setBulk("");
    } catch (e) { toast.error(errText(e, "Could not send the selection back")); }
    finally { setBusy(false); }
  };

  const doReturn = async (id) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/material/placements/${id}/return`,
        { qty: Number(ret.qty || 0), reason: ret.reason, note: ret.note, by: actor });
      await reset(`${res.return.quantity} unit(s) heading back to the shop`,
                  "It is not on hand again until the warehouse confirms receipt");
    } catch (e) { toast.error(errText(e, "Could not send it back")); }
    finally { setBusy(false); }
  };

  const roomLabel = [room?.room_number, room?.room_name].filter(Boolean).join(" · ") || "Room";

  // FLD-3 Delivery — the ticket's own lines for this room. Read first, act per line.
  // "Place here" covers what is still to account for PLUS anything already placed elsewhere, so
  // OFCI the owner already dropped (verified onsite) can still be spread into the right room.
  const delMovable = (r) => (r.unaccounted || 0)
    + Math.max((r.placements || []).reduce((s, p) => s + (Number(p.qty) || 0) - (Number(p.consumed_qty) || 0), 0), 0);
  const delLines = deliveryRoom
    ? (deliveryRoom.rows || []).filter((r) => deliveryRoom.keys.has(r.frs_item_key))
    : [];
  const delOpenIds = delLines.filter((r) => delMovable(r) > 0).map((r) => r.line_id);
  const delAct = async (fn) => { await fn(); await load(); };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="placement-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <MapPin className="w-4 h-4 text-blue-700" /> {roomLabel}
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            {deliveryRoom
              ? "Everything this delivery carries for this room, and where it is right now."
              : `Every unit on site for this room and where it is right now. Installation is recorded
                 from the completion check-off — it is never entered here.`}
          </DialogDescription>
        </DialogHeader>

        {deliveryRoom && (
          <div className="border border-gray-200 rounded" data-testid="delivery-room-lines">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
              <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-600">
                On this delivery — {delLines.length} line(s)
              </span>
              {!deliveryRoom.closed && (
                <Button size="sm" className="h-7 ml-auto" disabled={busy || !delOpenIds.length}
                        onClick={() => delAct(() => deliveryRoom.spread(delOpenIds))}
                        data-testid="delivery-room-place-all">
                  <MapPin className="w-3 h-3 mr-1" /> Place all of this room here
                </Button>
              )}
            </div>
            {delLines.length === 0 ? (
              <div className="px-2 py-4 text-center text-[11px] text-gray-500" data-testid="delivery-room-empty">
                This delivery carries nothing for this room.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {delLines.map((r) => (
                  <li key={r.line_id} className="px-2 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]"
                      data-testid={`delivery-room-line-${r.line_id}`}>
                    <span className="font-semibold text-gray-900">{r.product_short}</span>
                    {r.ofci && (
                      <span className="px-1 rounded text-[9px] font-semibold bg-amber-100 text-amber-900 border border-amber-300"
                            title="Owner furnished — the owner drops it in the room, you spread it">OFCI</span>
                    )}
                    <span className="text-gray-500 truncate max-w-[200px]">{r.description}</span>
                    <span className="tabular-nums text-gray-700">released <b>{r.released}</b></span>
                    <span className="tabular-nums text-gray-700">placed <b className="text-emerald-700">{r.delivered}</b></span>
                    <span className="tabular-nums text-gray-700">not delivered <b className="text-red-700">{r.not_delivered}</b></span>
                    <span className="tabular-nums text-gray-700">to account for <b>{r.unaccounted}</b></span>
                    {!deliveryRoom.closed && (
                      <span className="ml-auto inline-flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-7"
                                disabled={busy || delMovable(r) <= 0}
                                onClick={() => delAct(() => deliveryRoom.spread([r.line_id]))}
                                data-testid={`delivery-room-place-${r.line_id}`}>Place here</Button>
                        <select className={sel} defaultValue="" disabled={busy || !(r.unaccounted > 0)}
                                data-testid={`delivery-room-short-${r.line_id}`}
                                onChange={(e) => { const v = e.target.value; e.target.value = "";
                                                   if (v) delAct(() => deliveryRoom.short([r.line_id], v)); }}>
                          <option value="">Not delivered…</option>
                          {(deliveryRoom.reasons || []).map((x) => (
                            <option key={x} value={x}>{String(x).replace(/_/g, " ")}</option>
                          ))}
                        </select>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!data ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500" data-testid="placement-empty">
            Nothing is on site for this room.
          </div>
        ) : (
          <div className="space-y-2">
            {pickedIds.length >= 1 && (
              <div className="bg-slate-50 border border-slate-200 rounded p-2 space-y-2" data-testid="placement-bulk-panel">
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-700">
                  <span data-testid="placement-bulk-summary">
                    <b>{pickedIds.length}</b> selected · <b>{movableUnits}</b> unit(s) not installed
                    {movableIds.length < pickedIds.length && ` · ${pickedIds.length - movableIds.length} fully installed (stay put)`}
                  </span>
                  <Button size="sm" variant={bulk === "move" ? "default" : "outline"} className="h-7"
                          disabled={busy || movableUnits === 0} onClick={() => setBulk(bulk === "move" ? "" : "move")}
                          data-testid="placement-bulk-move">
                    <MapPin className="w-3 h-3 mr-1" /> Move to a new room / place
                  </Button>
                  <Button size="sm" variant={bulk === "return" ? "default" : "outline"} className="h-7"
                          disabled={busy || movableUnits === 0} onClick={() => setBulk(bulk === "return" ? "" : "return")}
                          data-testid="placement-bulk-return">
                    <PackageOpen className="w-3 h-3 mr-1" /> Return everything to the shop
                  </Button>
                  <button className="text-[11px] text-gray-500 underline ml-auto" onClick={() => { setPicked({}); setBulk(""); }}
                          data-testid="placement-clear-selection">Clear</button>
                </div>
                {bulk === "move" && (
                  <div className="space-y-1" data-testid="placement-bulk-move-panel">
                    <span className="text-[11px] text-gray-700">Move the {movableUnits} loose unit(s) to</span>
                    <DestinationPicker data={destData} entries={pickedEntries} value={dest} onChange={setDest}
                                       testId="placement-bulk-loc" />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] text-gray-700" data-testid="placement-bulk-move-preview">
                        {okToMove.reduce((s, r) => s + Number(r.qty || 0), 0)} unit(s) →{" "}
                        {[...new Set(okToMove.map((r) => r.to_label))].join(", ") || "—"}
                      </span>
                      <Button size="sm" className="h-8" disabled={busy || okToMove.length === 0} onClick={doGroupMove}
                              data-testid="placement-bulk-move-confirm">Confirm Move</Button>
                    </div>
                    <span className="text-[10px] text-gray-500 block">
                      A partly installed placement is split — the installed units stay where they are.
                    </span>
                  </div>
                )}
                {bulk === "return" && (
                  <div className="flex flex-wrap items-center gap-2" data-testid="placement-bulk-return-panel">
                    <span className="text-[11px] text-gray-700">Send {movableUnits} unit(s) back</span>
                    <select className={sel} value={ret.reason} data-testid="placement-bulk-return-reason"
                            onChange={(e) => setRet((c) => ({ ...c, reason: e.target.value }))}>
                      {RETURN_REASONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                    </select>
                    <Input className="h-7 w-48 text-xs" placeholder="Note" value={ret.note}
                           data-testid="placement-bulk-return-note"
                           onChange={(e) => setRet((c) => ({ ...c, note: e.target.value }))} />
                    <Button size="sm" className="h-7" disabled={busy} onClick={doBulkReturn}
                            data-testid="placement-bulk-return-confirm">Send it all back</Button>
                    <span className="text-[10px] text-gray-500 w-full">
                      Installed units stay. Nothing is on hand again until the warehouse confirms receipt.
                    </span>
                  </div>
                )}
              </div>
            )}
            {moveFor && (
              <MoveMaterialDialog projectId={projectId} actor={actor} entries={moveFor.entries} mode={moveFor.mode}
                                  viewFilter={viewFilter} title={moveFor.title}
                                  onClose={() => setMoveFor(null)}
                                  onDone={async () => { await load(); await loadMoves(); onChanged?.(); }} />
            )}
            {reopenFor && (
              <ReopenPrompt title={`Reopen ${reopenFor.placement.installed_qty} installed unit(s)?`} busy={busy}
                            testId="placement-reopen-installed-prompt" qty={reopenFor.qty}
                            max={reopenFor.placement.installed_qty}
                            onQty={(v) => setReopenFor((c) => ({ ...c, qty: v }))}
                            onCancel={() => setReopenFor(null)}
                            onConfirm={async ({ reason, note }) => {
                              setBusy(true);
                              try {
                                const { data: res } = await api.post("/material/installed/reopen", {
                                  project_id: projectId, frs_item_key: reopenFor.placement.frs_item_key,
                                  qty: Number(reopenFor.qty), reason, note, by: actor, confirm: true });
                                setReopenFor(null);
                                toast.success(`${res.qty} unit(s) reopened`, { description: res.message });
                                await load(); onChanged?.();
                              } catch (e) { toast.error(errText(e, "Could not reopen the installed quantity")); }
                              finally { setBusy(false); }
                            }}
                            body="The installed record stays in history. The units go back to on-site, uninstalled material so
                                  their location or delivery can be corrected — the room has to be completed again afterwards." />
            )}
            <table className="w-full text-[11px]" data-testid="placement-rows">
              <thead className="text-[10px] uppercase text-gray-500">
                <tr>
                  <th className="p-1 w-6">
                    <input type="checkbox" checked={allPicked} onChange={toggleAll} title="Select all"
                           ref={(el) => { if (el) el.indeterminate = pickedIds.length > 0 && !allPicked; }}
                           data-testid="placement-check-all" />
                  </th>
                  <th className="text-left p-1">Product</th>
                  <th className="text-right p-1">Qty</th>
                  <th className="text-left p-1">Location</th>
                  <th className="text-left p-1">Who / when</th>
                  <th className="text-right p-1">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <React.Fragment key={p.id}>
                    <tr className="border-t border-gray-200" data-testid={`placement-${p.id}`}>
                      <td className="p-1">
                        <input type="checkbox" checked={!!picked[p.id]}
                               data-testid={`placement-check-${p.id}`}
                               onChange={() => setPicked((c) => ({ ...c, [p.id]: !c[p.id] }))} />
                      </td>
                      <td className="p-1" data-testid={`placement-product-${p.id}`}>
                        <span>{p.description || p.product_code || p.product_short || "—"}</span>
                        {p.product_short && (p.description || p.product_code) && (
                          <span className="ml-1.5 font-mono text-[10px] text-gray-500">{p.product_short}</span>
                        )}
                        {p.installed_qty > 0 && (
                          <span className="ml-1 px-1 rounded text-[9px] font-semibold bg-emerald-100 text-emerald-900 border border-emerald-300"
                                data-testid={`placement-installed-${p.id}`}
                                title="Installed units stay put in bulk moves and returns">
                            {p.installed_qty} installed
                          </span>
                        )}
                        {p.is_surplus && (
                          <span data-testid={`placement-surplus-${p.id}`}
                                title="More is on site than the FRS now asks for"
                                className="ml-1 px-1 rounded text-[9px] font-semibold bg-amber-100 text-amber-900 border border-amber-300">
                            surplus
                          </span>
                        )}
                      </td>
                      <td className="p-1 text-right tabular-nums font-semibold"
                          data-testid={`placement-qty-${p.id}`}>{p.qty}</td>
                      <td className="p-1" data-testid={`placement-location-${p.id}`}>{p.location_label}</td>
                      <td className="p-1 text-gray-500">{p.by || "—"} · {when(p.at)}</td>
                      <td className="p-1 text-right whitespace-nowrap">
                        {/* FLD-PHOTO 3.2 — the finest level: THIS product at THIS location. */}
                        <button className="text-blue-700 mr-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 inline-flex items-center justify-center"
                                title={`Photos of this product at ${p.location_label}`}
                                data-testid={`placement-photos-${p.id}`}
                                onClick={() => setPhotosFor({ placement: p })}>
                          <Camera className="w-3.5 h-3.5" />
                        </button>
                        <button className="text-blue-700 mr-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 inline-flex items-center justify-center"
                                title="The material was shifted — pick where it went"
                                data-testid={`placement-move-${p.id}`}
                                onClick={() => setMoveFor({ entries: [entryOf(p)], mode: "own_room", title: "Where did it go?" })}>
                          <MapPin className="w-3.5 h-3.5" />
                        </button>
                        <button className="text-blue-700 mr-2" title="Part of it went elsewhere"
                                data-testid={`placement-split-${p.id}`}
                                onClick={() => { setOpenFor({ id: p.id, action: "split" }); setQty("1"); }}>
                          <Scissors className="w-3.5 h-3.5" />
                        </button>
                        {p.movable_qty > 0 && p.room_label && p.location_label !== p.room_label && (
                          <button className="text-emerald-700 mr-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 inline-flex items-center justify-center"
                                  title={`Moved to its assigned room ${p.room_label}`}
                                  data-testid={`placement-move-assigned-${p.id}`}
                                  onClick={() => setMoveFor({ entries: [entryOf(p)], mode: "own_room",
                                                              title: `Move into ${p.room_label}` })}>
                            <Home className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {undoableFor(p.id) && (
                          <button className="text-slate-700 mr-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 inline-flex items-center justify-center"
                                  title="Undo the whole move this came from"
                                  data-testid={`placement-undo-move-${p.id}`}
                                  onClick={() => doUndo(undoableFor(p.id).id)}>
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {p.installed_qty > 0 && (
                          <button className="text-blue-900 mr-2" title="Reopen the installed quantity (foreman / PM / admin)"
                                  data-testid={`placement-reopen-installed-${p.id}`}
                                  onClick={() => setReopenFor({ placement: p, qty: String(p.installed_qty) })}>
                            <LockOpen className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button className="text-amber-700" title="Send it back to the shop"
                                data-testid={`placement-return-${p.id}`}
                                onClick={() => { setOpenFor({ id: p.id, action: "return" });
                                                 setRet({ qty: String(p.qty), reason: "excess", note: "" }); }}>
                          <Undo2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                    {openFor?.id === p.id && (
                      <tr className="bg-gray-50">
                        <td colSpan={6} className="p-2" data-testid={`placement-panel-${openFor.action}-${p.id}`}>
                          {openFor.action === "return" ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[11px] text-gray-700">Send back</span>
                              <Input type="number" min="1" max={p.qty} className="h-7 w-16 text-xs text-right"
                                     value={ret.qty} data-testid={`placement-return-qty-${p.id}`}
                                     onChange={(e) => setRet((c) => ({ ...c, qty: e.target.value }))} />
                              <span className="text-[11px] text-gray-500">of {p.qty}</span>
                              <select className={sel} value={ret.reason}
                                      data-testid={`placement-return-reason-${p.id}`}
                                      onChange={(e) => setRet((c) => ({ ...c, reason: e.target.value }))}>
                                {RETURN_REASONS.map((r) => (
                                  <option key={r} value={r}>{r.replace(/_/g, " ")}</option>
                                ))}
                              </select>
                              <Input className="h-7 w-48 text-xs" placeholder="Note"
                                     value={ret.note} data-testid={`placement-return-note-${p.id}`}
                                     onChange={(e) => setRet((c) => ({ ...c, note: e.target.value }))} />
                              <Button size="sm" className="h-7" disabled={busy}
                                      onClick={() => doReturn(p.id)}
                                      data-testid={`placement-return-confirm-${p.id}`}>
                                <PackageOpen className="w-3 h-3 mr-1" /> Send it back
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7"
                                      onClick={() => setOpenFor(null)}>Cancel</Button>
                              <span className="text-[10px] text-gray-500 w-full">
                                It leaves the room now, but it is only on hand again once the
                                warehouse confirms receipt.
                              </span>
                            </div>
                          ) : (
                            <div className="flex flex-wrap items-center gap-2">
                              {openFor.action === "split" && (
                                <>
                                  <span className="text-[11px] text-gray-700">Split off</span>
                                  <Input type="number" min="1" max={p.qty - 1}
                                         className="h-7 w-16 text-xs text-right" value={qty}
                                         data-testid={`placement-split-qty-${p.id}`}
                                         onChange={(e) => setQty(e.target.value)} />
                                  <span className="text-[11px] text-gray-500">of {p.qty} to</span>
                                </>
                              )}
                              <LocationPicker value={loc} onChange={setLoc} rooms={data.rooms}
                                              areas={data.areas} testId={`placement-loc-${p.id}`} />
                              <Button size="sm" className="h-7" disabled={busy}
                                      onClick={() => doSplit(p.id)}
                                      data-testid={`placement-${openFor.action}-confirm-${p.id}`}>
                                Split it
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7"
                                      onClick={() => setOpenFor(null)}>Cancel</Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
      {photosFor && (
        <MaterialPhotos projectId={projectId} locationLabel={photosFor.placement.location_label}
                        locationType={photosFor.placement.location_type || ""}
                        roomKey={photosFor.placement.room_key || room?.key || ""}
                        roomLabel={photosFor.placement.room_label || ""}
                        placementId={photosFor.placement.id}
                        frsItemKey={photosFor.placement.frs_item_key || ""}
                        productShort={photosFor.placement.product_short || photosFor.placement.product_code || ""}
                        ticketId={photosFor.placement.ticket_id || ""}
                        actor={actor} role={role}
                        onClose={() => setPhotosFor(null)} onChanged={onPhotosChanged} />
      )}
    </Dialog>
  );
}
